// Super-admin bug-reports triage page.
//
// /super/bug-reports
//
// List + filter + search of tenant bug submissions. Click a row to
// open a detail drawer with:
//   - full description + auto-attached context
//   - editable triage notes
//   - status changer (open|triaged|in_progress|resolved|wontfix|duplicate)
//   - "Create JIRA ticket" button (manual trigger, posts to
//     /admin/bug-reports/:id/jira). Once linked, shows the JIRA key
//     as a clickable chip instead.

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box, Stack, Typography, TextField, Select, MenuItem, FormControl,
  InputLabel, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, Drawer, IconButton, Divider,
  CircularProgress, Snackbar, Alert as MuiAlert,
} from "@mui/material";
import { Search as SearchIcon, Close, Refresh as RefreshIcon, Launch as ExternalLink } from "@mui/icons-material";

import bugReportsAdmin from "../../../../utils/axios/v1/bugReportsAdminAxios";
import PageHeader from "../../../../components/pages/_new/super/common/PageHeader";
import colors from "../../../../utils/colors";

const SEVERITY_COLOR = {
  low: "#7B7B7B", medium: "#0255ED", high: "#F2B429", critical: "#FB3748",
};
const STATUS_COLOR = {
  open: "#0255ED",
  triaged: "#6371F7",
  in_progress: "#F2B429",
  resolved: "#1FC16B",
  wontfix: "#7B7B7B",
  duplicate: "#7B7B7B",
};

function Chiplet({ value, colorMap }) {
  return (
    <Chip
      label={value}
      size="small"
      sx={{
        bgcolor: (colorMap[value] || "#999") + "1F",
        color: colorMap[value] || "#999",
        fontWeight: 600,
        textTransform: "capitalize",
      }}
    />
  );
}

function DetailDrawer({ report, onClose, onUpdated, onSnack }) {
  const [working, setWorking] = useState(false);
  const [triageNotes, setTriageNotes] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (report) {
      setTriageNotes(report.triage_notes || "");
      setStatus(report.status);
    }
  }, [report]);

  if (!report) return null;

  const save = async () => {
    setWorking(true);
    try {
      const r = await bugReportsAdmin.update(report.id, {
        status,
        triage_notes: triageNotes || null,
      });
      onUpdated && onUpdated(r.data);
      onSnack && onSnack({ severity: "success", message: "Updated." });
    } catch (e) {
      onSnack && onSnack({ severity: "error", message: e?.response?.data?.message || e.message });
    } finally {
      setWorking(false);
    }
  };

  const createJira = async () => {
    setWorking(true);
    try {
      const r = await bugReportsAdmin.createJira(report.id);
      onUpdated && onUpdated(r.data);
      onSnack && onSnack({ severity: "success", message: `JIRA ticket created: ${r.data.jira_issue_key}` });
    } catch (e) {
      onSnack && onSnack({ severity: "error", message: e?.response?.data?.message || e.message });
    } finally {
      setWorking(false);
    }
  };

  return (
    <Drawer anchor="right" open={!!report} onClose={onClose} PaperProps={{ sx: { width: 640 } }}>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <Chiplet value={report.severity} colorMap={SEVERITY_COLOR} />
          <Chip label={report.category} size="small" />
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{report.title}</Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2, color: colors.neutral[700], fontSize: 12.5 }}>
          <span>id: {report.id.slice(0, 8)}…</span>
          <span>{new Date(report.created_at).toLocaleString()}</span>
          <span>{report.user_email}</span>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>DESCRIPTION</Typography>
        <Box sx={{ p: 1.5, bgcolor: colors.neutral[50], borderRadius: 1, fontSize: 13, whiteSpace: "pre-wrap" }}>
          {report.description}
        </Box>

        {(report.page_url || report.user_agent || report.viewport) && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>BROWSER CONTEXT</Typography>
            <Box sx={{ p: 1.5, bgcolor: colors.neutral[50], borderRadius: 1, fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace" }}>
              {report.page_url && <div><strong>URL:</strong> {report.page_url}</div>}
              {report.user_agent && <div><strong>UA:</strong> {report.user_agent}</div>}
              {report.viewport && <div><strong>Viewport:</strong> {report.viewport}</div>}
            </Box>
          </>
        )}

        {Array.isArray(report.console_errors) && report.console_errors.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
              RECENT CONSOLE ERRORS ({report.console_errors.length})
            </Typography>
            <Box sx={{ p: 1.5, bgcolor: "#1B1B1B", color: "#F4F4F4", borderRadius: 1, fontSize: 11, fontFamily: "'JetBrains Mono', monospace", maxHeight: 200, overflow: "auto" }}>
              {report.console_errors.map((e, i) => (
                <div key={i} style={{ marginBottom: 4 }}>
                  <span style={{ color: "#F2B429" }}>[{e.ts || ""}]</span> {e.message}
                  {e.source && <span style={{ color: "#7B7B7B" }}> ({e.source}:{e.lineno || "?"})</span>}
                </div>
              ))}
            </Box>
          </>
        )}

        <Divider sx={{ my: 2.5 }} />

        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Triage</Typography>

        <FormControl size="small" fullWidth sx={{ mb: 2 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {["open", "triaged", "in_progress", "resolved", "wontfix", "duplicate"].map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Triage notes"
          placeholder="Internal triage notes (not visible to the tenant)"
          multiline rows={4} fullWidth size="small"
          value={triageNotes}
          onChange={(e) => setTriageNotes(e.target.value)}
          inputProps={{ maxLength: 5000 }}
          sx={{ mb: 2 }}
        />

        <Stack direction="row" spacing={1.5}>
          <Button variant="contained" disabled={working} onClick={save}>
            {working ? "Saving…" : "Save"}
          </Button>
          <Box sx={{ flex: 1 }} />
          {report.jira_issue_key ? (
            <Chip
              icon={<ExternalLink fontSize="small" />}
              label={report.jira_issue_key}
              clickable
              component="a"
              href={report.jira_url}
              target="_blank"
              rel="noreferrer"
              color="primary"
              variant="outlined"
            />
          ) : (
            <Button variant="outlined" disabled={working} onClick={createJira}>
              Create JIRA ticket
            </Button>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
}

export default function T_BugReports() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [snack, setSnack] = useState(null);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [severity, setSeverity] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(0);
  const limit = 100;

  const refresh = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (q) params.q = q;
      if (status) params.status = status;
      if (severity) params.severity = severity;
      if (category) params.category = category;
      const r = await bugReportsAdmin.search(params);
      setRows(r.data.items);
      setTotal(r.data.total);
    } catch (e) {
      setSnack({ severity: "error", message: e?.response?.data?.message || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [q, status, severity, category, page]);

  const totalPages = Math.ceil(total / limit);

  return (
    <>
      <Helmet title="Bug Reports — Super Admin" />
      <Box sx={{ p: 3 }}>
        <PageHeader title="Bug Reports" />

        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">{total} reports</Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={refresh} disabled={loading} size="small"><RefreshIcon /></IconButton>
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            size="small" placeholder="Search title / description / email"
            value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
            sx={{ minWidth: 320 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
              <MenuItem value="">All</MenuItem>
              {Object.keys(STATUS_COLOR).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Severity</InputLabel>
            <Select label="Severity" value={severity} onChange={(e) => { setSeverity(e.target.value); setPage(0); }}>
              <MenuItem value="">All</MenuItem>
              {Object.keys(SEVERITY_COLOR).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Category</InputLabel>
            <Select label="Category" value={category} onChange={(e) => { setCategory(e.target.value); setPage(0); }}>
              <MenuItem value="">All</MenuItem>
              {["UI", "API", "Performance", "Data", "Other"].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>

        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                <TableCell sx={{ width: 120, fontWeight: 600 }}>Severity</TableCell>
                <TableCell sx={{ width: 120, fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ width: 110, fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ width: 130, fontWeight: 600 }}>JIRA</TableCell>
                <TableCell sx={{ width: 220, fontWeight: 600 }}>Submitter</TableCell>
                <TableCell sx={{ width: 150, fontWeight: 600 }}>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={7} sx={{ textAlign: "center", p: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} hover sx={{ cursor: "pointer" }} onClick={() => setOpenRow(r)}>
                  <TableCell sx={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</TableCell>
                  <TableCell><Chiplet value={r.severity} colorMap={SEVERITY_COLOR} /></TableCell>
                  <TableCell><Chiplet value={r.status} colorMap={STATUS_COLOR} /></TableCell>
                  <TableCell><Chip size="small" label={r.category} /></TableCell>
                  <TableCell>
                    {r.jira_issue_key ? (
                      <Chip
                        size="small" label={r.jira_issue_key} component="a"
                        href={r.jira_url} target="_blank" rel="noreferrer"
                        clickable color="primary" variant="outlined"
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : <span style={{ color: "#aaa", fontSize: 12 }}>—</span>}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{r.user_email}</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{new Date(r.created_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={7} sx={{ textAlign: "center", p: 4, color: "text.secondary" }}>No bug reports.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
            <Button size="small" disabled={page <= 0} onClick={() => setPage(page - 1)}>Prev</Button>
            <Typography variant="body2" sx={{ alignSelf: "center" }}>Page {page + 1} of {totalPages}</Typography>
            <Button size="small" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>Next</Button>
          </Stack>
        )}
      </Box>

      <DetailDrawer
        report={openRow}
        onClose={() => setOpenRow(null)}
        onUpdated={(updated) => {
          setRows((prev) => prev.map((r) => r.id === updated.id ? updated : r));
          setOpenRow(updated);
        }}
        onSnack={setSnack}
      />

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        {snack && <MuiAlert severity={snack.severity}>{snack.message}</MuiAlert>}
      </Snackbar>
    </>
  );
}
